import {PSSCENE_IMAGERY_CAPABILITIES_ASSETS} from '@src/constants/assetTypes.ts';
import {ITEM_TYPES} from '@src/constants/item-types.ts';
import {toAPIFilter} from '@src/utils/filters.ts';

const getSingleItemIdSearchFilter = ({id, selectedItemTypes}) => {
  // Handle search for single item ID
  const singleItemFilter = {
    type: 'StringInFilter',
    field_name: 'id',
    config: [id],
  };

  // Use the item type to allow for updating legacy item types when searching
  // by single item id. This will trigger a change to the filter in
  // worker-manager
  const itemTypeFilter = {
    type: 'StringInFilter',
    field_name: 'item_type',
    config: selectedItemTypes,
  };

  return {
    type: 'AndFilter',
    config: [singleItemFilter, itemTypeFilter],
  };
};

export const isEmptyRange = range => range[0] === null && range[1] === null;

const getDateRangeFilters = dateRanges => {
  if (!dateRanges || dateRanges.length === 0) {
    return null;
  }

  const sortedDateRanges = [...dateRanges].sort((a, b) =>
    a > b ? 1 : b > a ? -1 : 0,
  );

  const config = sortedDateRanges.map(dates => {
    const dateConfig = {
      type: 'DateRangeFilter',
      field_name: 'acquired',
      config: {
        gte: dates[0],
      },
    };

    if (dates[1]) {
      dateConfig.config.lte = dates[1];
    }

    return dateConfig;
  });

  return {
    type: 'OrFilter',
    config,
  };
};

const getItemTypeFilter = ({itemType}) => {
  return {
    type: 'StringInFilter',
    field_name: 'item_type',
    config: [itemType],
  };
};

/**
 * Generate search filters for PSScene items
 * @param {array} capabilities - An array of IMAGERY_CAPABILITIES
 * @return {object} search filter - A Search filter object
 * @requires module:@src/constants/asset-types.js/PSSCENE_IMAGERY_CAPABILITIES_ASSETS
 * @requires module:@src/constants/item-types.ts/ITEM_TYPES
 */
export const getPSSceneAssetFilter = capabilities => {
  // Calculate the asset to filter by
  const PSSceneAssetFilters = capabilities.map(
    cap => PSSCENE_IMAGERY_CAPABILITIES_ASSETS[cap],
  );

  // Don't use the asset filter for "Visual" capability, since all results have visual capability
  // and we don't want to add the overhead cost of using the asset filter in the search query
  // since it will make no difference
  if (PSSceneAssetFilters.length) {
    return {
      type: 'AndFilter',
      config: PSSceneAssetFilters.map(filter => ({
        type: 'AssetFilter',
        config: [filter],
      })),
    };
  }

  return null;
};

const getInstrumentFilter = ({instruments, itemType}) => {
  if (instruments.length > 0 && itemType === ITEM_TYPES.PSSCENE) {
    return {
      type: 'StringInFilter',
      field_name: 'instrument',
      config: instruments,
    };
  }
};

const getPublishStageFilter = ({publishingStages, itemType}) => {
  if (publishingStages.length > 0 && itemType === ITEM_TYPES.PSSCENE) {
    return {
      type: 'StringInFilter',
      field_name: 'publishing_stage',
      config: publishingStages,
    };
  }
};

const getFiltersForItemType = ({
  itemType,
  instruments,
  publishingStages,
  selectedImageryCapabilities,
}) => {
  const itemTypeFilter = getItemTypeFilter({
    itemType,
  });

  if (itemType === ITEM_TYPES.PSSCENE) {
    const instrumentFilter = getInstrumentFilter({
      instruments,
      itemType,
    });

    const publishStageFilter = getPublishStageFilter({
      itemType,
      publishingStages,
    });

    const assetFilter = getPSSceneAssetFilter(selectedImageryCapabilities);
    return {
      type: 'AndFilter',
      config: [
        itemTypeFilter,
        assetFilter,
        instrumentFilter,
        publishStageFilter,
      ].filter(f => !!f),
    };
  }

  return itemTypeFilter;
};

export const getSearchFilters = ({
  dateRanges,
  generalFilters = [],
  itemId,
  pssceneFilters = {},
  selectedItemTypes,
}) => {
  const searchFiltersList = [];

  const getFilteredList = field =>
    Object.keys(pssceneFilters[field] ?? {}).filter(
      key => !!pssceneFilters[field][key],
    );

  const addFilter = filter => {
    if (filter) {
      searchFiltersList.push(filter);
    }
  };

  if (itemId) {
    addFilter(
      getSingleItemIdSearchFilter({
        id: itemId,
        selectedItemTypes,
      }),
    );
  } else {
    // Handle regular search
    const instruments = getFilteredList('instruments');
    const publishingStages = getFilteredList('publishingStages');

    // Note: Running date filters first in query helps Data API ES backend
    // (not sure if this is still true)
    addFilter(getDateRangeFilters(dateRanges));

    (generalFilters ?? []).forEach(filter => addFilter(toAPIFilter(filter)));

    addFilter({
      type: 'OrFilter',
      config: selectedItemTypes.map(itemType =>
        getFiltersForItemType({
          instruments,
          itemType,
          publishingStages,
          selectedImageryCapabilities: pssceneFilters.imageryCapabilities ?? [],
        }),
      ),
    });
  }

  return {
    type: 'AndFilter',
    config: searchFiltersList,
  };
};
